import { useEffect, useMemo, useState, type ReactNode } from "react";
import { trackReplayUsed } from "../lib/analytics";
import { isReplayKeyBlocked } from "../lib/replayKeyGuard";
import {
  initReplayStep,
  jumpToDecision,
  jumpToResolution,
  scrubTo,
  stepReplay,
  type ReplayStep,
} from "../lib/replayStep";
import { hasMeaningfulVolume } from "../lib/volume";
import { useActiveLoop } from "../state/loops";
import { isDialogOpen } from "../state/dialogStore";
import type { LoopState } from "../state/roundLoop";
import type { Candle } from "../types";
import { Centered } from "./Centered";
import { Chart, type OverlayLine } from "./Chart";
import { ChartSummary } from "./ChartSummary";
import { Container } from "./Container";
import { ReplayControls } from "./ReplayControls";
import { formatRoundTime } from "./RevealBanner";
import { SettingsBar, type Market } from "./SettingsBar";
import { ToolsDrawer } from "./ToolsDrawer";
import { useTAWorkbench } from "./useTAWorkbench";

/**
 * What a Round must carry for the shell to render the chrome around it: an id
 * (Round selection, TA workbench keying) and the reveal-only Instrument
 * identity (the SettingsBar market list).
 */
export type PlayableRound = {
  id: string;
  instrumentId: string;
  instrumentName: string;
  dateRange: { start: string; end: string };
};

/**
 * The chart's top-left caption once revealed: which Instrument this was and
 * when (GRA-124 named it in the reveal banner; it reads better on the chart
 * it describes, above the OHLC readout).
 */
function roundCaption(round: PlayableRound): string {
  return `${round.instrumentName} (${formatRoundTime(round.dateRange.start)} → ${formatRoundTime(round.dateRange.end)})`;
}

export type LoopShellProps<R extends PlayableRound, C, V> = {
  /** The Loop's store, from `createRoundLoop` (see ADR-0008). */
  store: <T>(selector: (state: LoopState<R, C, V>) => T) => T;
  /** Per-Loop loading copy (e.g. "loading rounds…", "loading weekly rounds…"). */
  loadingMessage: string;
  /**
   * "from-bank": derive the SettingsBar market list from the bank's index.
   * "none": pass an empty list (a Loop that offers no market filter).
   */
  markets: "from-bank" | "none";
  /** The Context candles to chart (trend Loops slice by the Context-length setting). */
  chartContext: (round: R) => Candle[];
  /** The Forward candles to chart. */
  chartForward: (round: R, revealed: boolean) => Candle[];
  /** Price-line overlays (barriers, SL/TP); the Loop decides what they mean. */
  overlays: (s: { commit: C | null; reveal: V | null; revealed: boolean }) => OverlayLine[];
  /**
   * Called when the player drags one of those overlays that opted into it
   * (`draggable`), with the line and the price it now sits at. The Loop owns
   * the value: it is expected to hand the moved line straight back through
   * `overlays`, so the chart and whatever field holds the number cannot drift
   * apart.
   */
  onOverlayDrag?: (overlay: OverlayLine, value: number) => void;
  /**
   * Where the round actually resolved, as a count of Forward candles from the
   * decision point (GRA-147): a Trend barrier's `daysToResolve`, a Trade's
   * `barsHeld`, or 1 for the single Next-Candle answer. The reveal opens
   * there instead of at the end of the Forward, and Jump-to-resolution
   * returns to it. Omitted, the shell falls back to the full Forward (the
   * old GRA-136 behavior), which is right for any Loop with no earlier
   * resolution to speak of.
   */
  resolutionStep?: (s: { round: R; commit: C | null; reveal: V | null }) => number;
  /**
   * The history/session toggle buttons (HistoryPanel.tsx's `HistoryToggles`),
   * rendered in the shared utility row beside settings and tools. Optional
   * so a caller with nothing to offer there (none currently) costs nothing;
   * every real view passes the same pair, since the round history spans
   * every Loop rather than belonging to one.
   */
  historyToggles?: ReactNode;
  /** What `historyToggles` opens (`HistoryPanel.tsx`'s `HistoryPanels`), rendered beneath the row. */
  historyPanels?: ReactNode;
  /** The footer: entry controls before commit, the Reveal banner after. */
  footer: (s: {
    round: R;
    context: Candle[];
    commit: C | null;
    reveal: V | null;
    revealed: boolean;
    submit: (commit: C) => void;
    next: () => void;
    /** The player's own reason note (GRA-135), typed before committing. */
    note?: string;
    setNote?: (note: string) => void;
  }) => ReactNode;
};

/** Shared chart, controls, and reveal lifecycle for all three game views. */
export function LoopShell<R extends PlayableRound, C, V>({
  store,
  loadingMessage,
  markets,
  chartContext,
  chartForward,
  overlays,
  onOverlayDrag,
  resolutionStep,
  historyToggles,
  historyPanels,
  footer,
}: LoopShellProps<R, C, V>) {
  const status = store((s) => s.status);
  const current = store((s) => s.current);
  const instruments = store((s) => s.instruments);
  const phase = store((s) => s.phase);
  const commit = store((s) => s.commit);
  const reveal = store((s) => s.reveal);
  const warning = store((s) => s.warning);
  const submit = store((s) => s.submit);
  const next = store((s) => s.next);
  const note = store((s) => s.note);
  const setNote = store((s) => s.setNote);

  // Needed above the loading/error guard below since the replay effect (and
  // its keyboard handler) reads it; `current` can still be null on the first
  // render, before a bank has loaded.
  const forward = current ? chartForward(current, phase === "revealed") : [];
  const forwardLen = forward.length;

  // Where this round's own resolution sits, clamped into the Forward that's
  // actually charted (a caller's raw daysToResolve/barsHeld can run past a
  // trimmed Forward, e.g. a barrier Loop's nMax slice). Falls back to
  // `forwardLen` (the fully-revealed round) for any Loop that doesn't supply
  // one, so it opens exactly where GRA-136 always did.
  const rawResolutionStep = current
    ? resolutionStep?.({ round: current, commit, reveal }) ?? forwardLen
    : 0;
  const clampedResolutionStep = Math.max(0, Math.min(rawResolutionStep, forwardLen));

  // Candle-by-candle replay through the revealed Forward (GRA-136), now with
  // jumps and a scrubber that opens on the resolution candle (GRA-147).
  // `null` means "not replayable yet": either the round isn't revealed, or
  // the reveal animation is still running: Chart only hands back a count
  // once it fires onRevealComplete, so there's nothing to step through before
  // that without racing the animation. Reset on every Round change (not on
  // `revealed` toggling) so it can't race that same onRevealComplete call: a
  // reset effect keyed on `revealed` would fire on the very same commit as
  // the instant-reveal (`revealStepMs`-less-than-a-frame) case and could
  // clobber the freshly-set step count back to null.
  const [replay, setReplay] = useState<ReplayStep | null>(null);
  useEffect(() => {
    setReplay(null);
  }, [current?.id]);

  useEffect(() => {
    if (phase !== "revealed") return;
    const handler = (event: KeyboardEvent) => {
      // Next and the replay-step keys go quiet while a dialog is open (GRA-146).
      if (isDialogOpen()) return;
      if (event.key === "Enter") {
        event.preventDefault();
        next();
        return;
      }
      // Replay keys (arrows, Home, End) do nothing before the reveal has
      // settled or while a dialog is open over the page (GRA-147): a held
      // Home/End inside, say, the info dialog must not silently move the
      // chart underneath it.
      const isReplayKey =
        event.key === "ArrowLeft" ||
        event.key === "ArrowRight" ||
        event.key === "Home" ||
        event.key === "End";
      if (!isReplayKey) return;
      if (replay === null || isReplayKeyBlocked()) return;
      event.preventDefault();
      if (event.key === "ArrowLeft") {
        trackReplayUsed({ control: "step" });
        setReplay((s) => (s ? stepReplay(s, -1) : s));
      } else if (event.key === "ArrowRight") {
        trackReplayUsed({ control: "step" });
        setReplay((s) => (s ? stepReplay(s, 1) : s));
      } else if (event.key === "Home") {
        trackReplayUsed({ control: "jump" });
        setReplay((s) => (s ? jumpToDecision(s) : s));
      } else {
        trackReplayUsed({ control: "jump" });
        setReplay((s) => (s ? jumpToResolution(s) : s));
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [phase, next, replay]);

  // The instrument list comes off the bank's index (GRA-152), not off a
  // decoded bank: the chips only need every instrument the bank carries, and
  // that is exactly what the index's header table already says.
  const marketList = useMemo<Market[]>(
    () => (markets === "none" ? [] : instruments),
    [markets, instruments],
  );

  const ta = useTAWorkbench(current?.id);
  // Only for the chart's text alternative: which game these candles belong to.
  const activeLoop = useActiveLoop();

  if (status === "loading") return <Centered>{loadingMessage}</Centered>;
  if (status === "error" || !current) {
    // A player, not a developer, is the one who reads this: the charts load
    // over the network, so the everyday cause is a dropped connection rather
    // than a bank that was never built. Retry re-runs the Loop's own `init`,
    // which is idempotent, so a recovered connection is one tap away instead
    // of a manual page reload.
    return (
      <Centered>
        <div className="flex flex-col items-center gap-4">
          <p>Couldn&rsquo;t load the charts. Check your connection and try again.</p>
          <button
            type="button"
            onClick={() => void activeLoop.init()}
            className="flex min-h-8 items-center rounded border border-accent px-4 py-1 font-semibold text-accent transition-colors hover:border-accent/70 sm:min-h-11"
          >
            Retry
          </button>
        </div>
      </Centered>
    );
  }

  const revealed = phase === "revealed";
  const context = chartContext(current);
  const overlayLines = overlays({ commit, reveal, revealed });
  // The whole round, Context and Forward, decides whether the VOL toggle has
  // anything to draw, matching what Chart itself checks (GRA-133, GRA-148).
  const volumeAvailable = hasMeaningfulVolume([...context, ...forward]);

  // Only let the step controls actually be used once there's a settled
  // reveal to step through: `replay` stays null until Chart's
  // onRevealComplete fires, so this also covers "the animation is still
  // running" without a separate flag.
  const canStep = revealed && replay !== null && forwardLen > 0;
  const step = replay?.step ?? 0;

  // Whether the row is shown AT ALL: `revealed && forwardLen > 0`, not
  // `canStep` — reserved the moment the round reveals, rather than mounted
  // once the reveal animation actually settles a few hundred ms later. Chart
  // resizes its pane to whatever height is left above the footer (see
  // ChartSummary/Chart's flex sizing), so a row that pops in mid-reveal grows
  // the footer and visibly shrinks/reflows every already-drawn candle right
  // as the last one lands. Showing the (disabled, GRA-147) row from the
  // first revealed frame instead means that reflow happens once, before any
  // Forward candle has been painted, and the animation that follows never
  // moves the chart's own box again.
  const showReplayRow = revealed && forwardLen > 0;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* One chrome strip for all four collapsibles (GRA-131, GRA-148): the
          Tools drawer rides in the settings row, and the history/session
          toggles ride the same row right-aligned, rather than each standing
          as its own second bar. A closed drawer or panel costs nothing above
          the chart. */}
      <SettingsBar
        markets={marketList}
        historyToggles={historyToggles}
        historyPanels={historyPanels}
      >
        <ToolsDrawer
          onTool={ta.onTool}
          onClear={ta.onClear}
          indicators={ta.indicators}
          onToggleIndicator={ta.onToggleIndicator}
          activeTool={ta.activeTool}
          volumeAvailable={volumeAvailable}
        />
      </SettingsBar>
      {warning && (
        <p role="alert" className="px-4 py-1 text-center font-mono text-xs text-accent">
          {warning}
        </p>
      )}
      {/* Flex the whole way down rather than `h-full`: below `sm` the shell is
          `min-h-screen`, so a percentage height has no definite ancestor to
          resolve against and the chart would sit at its floor with dead space
          under it. */}
      <main className="flex min-h-0 flex-1 flex-col pt-2 sm:pt-4">
        {/* `min-h-0` matters as much as `flex-1`: without it this row keeps
            `min-height: auto` and refuses to shrink below the chart it holds,
            so opening the settings strip pushed the chart down over the footer
            instead of shortening it. */}
        <Container className="flex min-h-0 flex-1 flex-col">
          {/* The chart never shrinks below a readable height: on a phone the
              chrome above and below it would otherwise squeeze it to nothing,
              and the page scrolls instead (App's `min-h-screen`). That floor
              is 11rem on a phone, not 16 (GRA-131): 16rem was more than the
              space a 390×844 screen has left under the trade panel, so the
              floor itself was what pushed "Place trade" off the first screen.
              At 11rem the chart takes whatever is actually spare (still ~15rem
              in Trade, ~28rem in Predict) and the page stops scrolling. */}
          {/* A named region with a text alternative inside it (GRA-148): the
              canvas itself exposes nothing, so without these the chart was an
              unnamed hole in the middle of the page. `min-h-52` is the phone
              floor (up from 11rem): under it 50 context candles compress to a
              smear, and the chrome above now leaves the room for it. */}
          <div
            role="region"
            aria-label="price chart"
            className="relative flex min-h-52 flex-1 overflow-hidden rounded-lg border border-muted/20 py-2 sm:min-h-0 sm:py-4"
          >
            <ChartSummary
              modeLabel={activeLoop.shareLabel}
              context={context}
              forward={forward}
              revealed={revealed}
              overlays={overlayLines}
            />
            <Chart
              ref={ta.chartRef}
              context={context}
              forward={forward}
              revealed={revealed}
              overlays={overlayLines}
              onOverlayDrag={onOverlayDrag}
              caption={roundCaption(current)}
              indicators={ta.indicators}
              onDrawingChange={ta.onDrawingChange}
              revealTo={clampedResolutionStep}
              onRevealComplete={() => setReplay(initReplayStep(clampedResolutionStep, forwardLen))}
              forwardStepOverride={revealed && replay !== null ? replay.step : null}
            />
          </div>
        </Container>
      </main>
      <footer className="pt-5 pb-3 sm:pt-8 sm:pb-4">
        {/* Vertical flex with a gap, rather than each piece carrying its own
            ad hoc margin, so the replay row and the mode's banner/controls
            below it keep one consistent rhythm regardless of which of those
            is actually present. */}
        <Container className="flex flex-col gap-y-5 sm:gap-y-8">
          {/* Replay controls (GRA-136, extended by GRA-147 with jumps and a
              scrubber): reserved from the first revealed frame (see
              `showReplayRow`) so the footer's height, and so the chart's own
              box, only change once — not mid-animation. Disabled (`!canStep`)
              until the reveal has actually settled into a `ReplayStep`, so a
              click can't race the one-shot reveal it's laid over. Buttons
              carry the same key hints GuessControls and RevealBanner use
              elsewhere in this footer. */}
          {showReplayRow && (
            <ReplayControls
              step={step}
              max={forwardLen}
              resolutionStep={clampedResolutionStep}
              disabled={!canStep}
              onDecision={() => {
                trackReplayUsed({ control: "jump" });
                setReplay((s) => (s ? jumpToDecision(s) : s));
              }}
              onStep={(delta) => {
                trackReplayUsed({ control: "step" });
                setReplay((s) => (s ? stepReplay(s, delta) : s));
              }}
              onResolution={() => {
                trackReplayUsed({ control: "jump" });
                setReplay((s) => (s ? jumpToResolution(s) : s));
              }}
              onScrub={(value) => {
                trackReplayUsed({ control: "scrub" });
                setReplay((s) => (s ? scrubTo(s, value) : s));
              }}
            />
          )}
          {footer({
            round: current,
            context,
            commit,
            reveal,
            revealed,
            submit,
            next,
            note,
            setNote,
          })}
        </Container>
      </footer>
    </div>
  );
}
